/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  API_STATUS_CODE,
  AddToCartType,
  CouponTarget,
  Languages
} from '@/constants'
import { useLocalStorage } from '@/hooks'
import useGTMDataLayer from '@/hooks/useGTMDAtaLayer'
import type {
  GiftItems,
  GuestInfo,
  OrderService,
  Product,
  QueryParam,
  VoucherItem,
} from '@/types'
import { type CartItemType, type Order, type PaymentMethod } from '@/types'
import { api } from '@/utils'
import { getTranslate } from '@/utils/api-interceptors'
import { collectProductIds } from '@/utils/cart-helpers'
import * as pixel from '@/utils/fpixel'
import { convertQueryParam, delay, scrollToTop } from '@/utils/next-util'
import constate from 'constate'
import { getCookie } from 'cookies-next'
import { isEmpty, sortBy } from 'lodash'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from './AuthContainer'
import { useNotification } from './NotificationContainer'
const CartContainer = () => {
  const [cartLoading, setCartLoading] = useState(false)
  const [orderInfo, setOrderInfo] = useState<Order | null>(null)
  const [guestInfo, setGuestInfo] = useLocalStorage<GuestInfo | null>(
    'guestInfo',
    {
      areaType: 'old',
      provinceCode: '',
      districtCode: '',
      saleRegionCode: '',
      wardCode: '',
      address: '',
      name: '',
      phone: '',
      gender: 'male',
    }
  )
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout>>()
  const [statusCode, setStatusCode] = useState('')
  const [listCoupons, setListCoupons] = useState<VoucherItem[]>()
  const [cartCoupons, setCartCoupons] = useState<VoucherItem[]>()
  const [isPreOrder, setIsPreOrder] = useState<boolean>(false)
  const [isInstallment, setIsInstallment] = useState<boolean>(false)
  const [productLoading, setProductLoading] = useState('')
  const [voucherApply, setVoucherApply] = useLocalStorage<string[]>(
    'applyVoucher',
    []
  )
  const {
    cart,
    getGuessTokenCart,
    fetchCart,
    setCart,
    setErrorAuth,
    token,
    refetch,
    user,
    getPaymentMethodAvailable,
    setGuessPhone,
    guessPhone,
  } = useAuth()
  const [paymentMethodCart, setPaymentMethodCart] =
    useState<PaymentMethod | null>(null)
  const [couponApply, setCouponApply] = useState<string[]>([])
  const [autoApplySnapshot, setAutoApplySnapshot] = useState('')
  const [cartProductsSignature, setCartProductsSignature] = useState('')
  const [skipAutoApply, setSkipAutoApply] = useState(false)
  const [invalidCouponSnapshot, setInvalidCouponSnapshot] = useState('')
  const clearAutoVoucherState = useCallback(
    (options: { skipAutoApply?: boolean } = {}) => {
      setCouponApply([])
      setVoucherApply([])
      setAutoApplySnapshot('')
      if (options.skipAutoApply) {
        setSkipAutoApply(true)
      }
    },
    [setVoucherApply, setAutoApplySnapshot, setSkipAutoApply]
  )
  const resumeAutoApplyVoucher = useCallback(() => {
    setSkipAutoApply(false)
    setAutoApplySnapshot('')
  }, [refetch])
  const handleFetchCartError = useCallback(
    (error: unknown, params: Record<string, unknown>, errorKey: string) => {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
          ? error
          : 'Đã có lỗi xảy ra'
      if (message.includes('_orderCoupon')) {
        clearAutoVoucherState()
        const rest: Record<string, unknown> = { ...params }
        delete rest.coupon
        rest.action = `${errorKey}-coupon-reset`
        fetchCart(rest).catch(innerError => {
          setErrorAuth({
            status: true,
            message:
              innerError instanceof Error
                ? innerError.message
                : 'Làm mới giỏ hàng thất bại',
            key: `${errorKey}-coupon-reset`,
          })
        })
        return
      }
      setErrorAuth({
        status: true,
        message,
        key: errorKey,
      })
    },
    [clearAutoVoucherState, fetchCart, setErrorAuth]
  )
  const safeFetchCart = useCallback(
    (params: Record<string, unknown> = {}, errorKey = 'fetchCart-default') => {
      return fetchCart(params as any).catch(error => {
        handleFetchCartError(error, params, errorKey)
      })
    },
    [fetchCart, handleFetchCartError]
  )
  const { showProgressBar, hideProgressBar } = useNotification()
  const router = useRouter()
  const { pushAddToCartEvent } = useGTMDataLayer()
  const { mutate: addToCards } = useMutation(
    'addToCards',
    async (values: { product: any; quantity: number }) => {
      const token = getGuessTokenCart()
      showProgressBar()
      return api(`api/shopping-cart`, {
        method: 'POST',
        baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
        data: {
          productId: values.product.productId,
          shippingMethod: values.product.shippingMethod,
          quantity: values.quantity,
          serviceIds: values?.product?.serviceIds,
          giftItems: values?.product?.giftItems,
          selectedReceiveAtWarehouse:
            values?.product?.selectedReceiveAtWarehouse,
        },
        ...(token ? { headers: { uuid: token } } : {}),
      }).then(res => {
        if (res instanceof Error) {
          throw new Error((res as any)?.statusCode)
        }
        return res
      })
    }
  )
  useEffect(() => {
    if (voucherApply?.length > 0) {
      setCouponApply(voucherApply)
    }
    // if (!token) {
    //   setCouponApply([])
    // }
  }, [voucherApply, token])

  useEffect(() => {
    if (router?.query?.type == 'preOrder') {
      setIsPreOrder(true)
    }
  }, [router])
  useEffect(() => {
    if (!user && guestInfo) {
      const newPhone = guestInfo.phone || null
      // Chỉ gọi getPaymentMethodAvailable khi số điện thoại thay đổi
      if (newPhone !== guessPhone) {
        setGuessPhone(newPhone)
        getPaymentMethodAvailable()
      }
    }
  }, [guestInfo, user, guessPhone])

  const { mutate: addToInstallment } = useMutation(
    'addInstallment',
    async (values: { product: any; quantity: number; productIds: string }) => {
      const token = getGuessTokenCart()
      showProgressBar()
      const params = values?.productIds
        ? 'productIds=' + values?.productIds + ':1'
        : ''
      return api(`api/shopping-cart?${params}`, {
        method: 'POST',
        baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
        data: {
          isInstallment: true,
          productId: values?.product?.productId,
          quantity: values?.quantity,
          serviceIds: values?.product?.serviceIds,
          giftItems: values?.product?.giftItems,
          selectedReceiveAtWarehouse:
            values?.product?.selectedReceiveAtWarehouse,
        },
        ...(token ? { headers: { uuid: token } } : {}),
      }).then(res => {
        if (res instanceof Error) {
          throw new Error((res as any)?.statusCode)
        }
        return res
      })
    }
  )
  const { mutate: addToPreOrder } = useMutation(
    'addPreOrder',
    async (values: { product: any; quantity: number }) => {
      showProgressBar()
      const token = getGuessTokenCart()
      return api(`api/shopping-cart`, {
        method: 'POST',
        baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
        data: {
          productId: values?.product?.productId,
          isPreOrder: true,
          quantity: values?.quantity,
          serviceIds: values?.product?.serviceIds,
          giftItems: values?.product?.giftItems,
          selectedReceiveAtWarehouse:
            values?.product?.selectedReceiveAtWarehouse,
        },
        ...(token ? { headers: { uuid: token } } : {}),
      }).then(res => {
        if (res instanceof Error) {
          throw new Error((res as any)?.statusCode)
        }
        return res
      })
    }
  )
  const { mutate: addBundleToCarts } = useMutation(
    'addBundleToCarts',
    async (bundleProducts: any) => {
      // const token = getGuessTokenCart()
      showProgressBar()
      return api(`api/shopping-carts`, {
        method: 'POST',
        baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
        data: bundleProducts,
      }).then(res => {
        if (res instanceof Error) {
          throw new Error((res as any)?.statusCode)
        }
        return res
      })
    }
  )
  const getOrderDetail = async (orderId: string) => {
    try {
      const res = await api(`api/order/${orderId}/detail`, {
        method: 'get',
        baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
      })
      if (res?.data) {
        setOrderInfo(res.data)
      }
    } catch (error) {
      console.error('Error fetching order detail:', error)
    }
  }
  const getListCoupons = async () => {
    const res = await api(`api/profile/coupons`, {
      method: 'get',
      baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
    })
    return setListCoupons(res.data)
  }
  const getCartCoupons = useCallback(async (productIds?: string[]) => {
    let params = null
    const queryParams = {
      ...(productIds ? { productIds: productIds } : {}),
    }
    if (!isEmpty(queryParams) && queryParams) {
      params = convertQueryParam(queryParams as QueryParam)
    }
    const res = await api(`api/shopping-cart/coupons?${params || ''}`, {
      method: 'get',
      baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
    })

    setCartCoupons(sortBy(res.data, ['product._id']))
  }, [])
  const cancelOrder = async (order?: Order | null) => {
    if (!order) {
      return
    }
    await api(`api/order/${order?._id}`, {
      method: 'delete',
      baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
    }).then(e => {
      setCartLoading(true)
      return e
    })

    toast.success(
      getCookie('language') === Languages.EN
        ? 'Cancel order successfully'
        : 'Hủy đơn hàng thành công',
      {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
        theme: 'light',
      }
    )
    await api(
      `api/order/${router.query.orderNumberId || router.query.orderId}/detail`,
      {
        method: 'get',
        baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
      }
    ).then(e => {
      setOrderInfo(e.data)
    })
    fetchCart()
    scrollToTop()
  }

  const confirmPayment = async (order?: Order | null) => {
    if (!order) {
      return
    }
  }

  const choosePaymentMethod = async (
    order?: Order | null,
    paymentMethod?: PaymentMethod | null,
    reloadCart?: boolean
  ) => {
    if (!order) {
      return
    }
    setCartLoading(true)
    if (paymentMethod && paymentMethod.isOnline) {
      const res = await api(`api/payment/link`, {
        method: 'POST',
        baseURL: process.env.NEXT_PUBLIC_FINANCE_API_URL,
        data: {
          paymentChannel: paymentMethod?.value,
          paymentType: paymentMethod?.paymentType,
          bankType: paymentMethod?.bank_type,
          bankCode: '',
          orderCode: order?.code,
          amount: order?.totalAmount,
          currency: 'VND',
          redirectUrl:
            window.location.origin +
            '/checkout/confirm?orderNumberId=' +
            order?._id,
          transactionPurpose: '',
        },
      })
      setStatusCode(res?.statusCode)
      if (res && !(res instanceof Error)) {
        if (res.data) {
          return router.push(res?.data)
        } else {
          setCartLoading(false)
          throw new Error(res?.statusCode)
        }
      }
    } else {
      await api(`api/order/${order?._id}`, {
        method: 'put',
        baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
        data: {
          payment: {
            method: paymentMethod?.value,
          },
        },
      })
    }

    if (reloadCart) fetchCart()
    setCartLoading(false)
    await delay(1000)
    scrollToTop()
  }

  const chooseDestination = async (districtCode: string, productId: string) => {
    if (!districtCode || !productId) {
      return
    }
    setCartLoading(true)
    const res = await api(`api/shipment/shipping-rate`, {
      method: 'POST',
      baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
      data: {
        productId,
        destinationCode: districtCode,
      },
    }).catch(() => {
      setCartLoading(false)
    })
    await delay(200)
    setCartLoading(false)
    return res.data.shippingRate
  }

  const resolveActiveCoupons = () =>
    couponApply.length > 0 ? couponApply : voucherApply

  const addToCart = async (product: any, type: string) => {
    const cartItemData = {
      code: product.code,
      name: product.name,
      price: product.price,
      slugs: product.slugs,
      productId: product._id,
      quantity: 1,
      shippingMethod: product.shippingMethod,
      finalPrice: product.finalPrice,
      serviceIds: product?.addService,
      giftItems: product?.addGift,
      selectedReceiveAtWarehouse: product?.selectedReceiveAtWarehouse,
    }
    setCartLoading(true)
    setProductLoading(product?._id)

    return new Promise((resolve, reject) => {
      if (type === AddToCartType.PRE_ORDER) {
        addToPreOrder(
          {
            product: cartItemData,
            quantity: 1,
          },
          {
            onSuccess: async res => {
              setCart({
                ...res.data,
                quantity: res?.data?.products?.reduce(
                  (acc: number, e: CartItemType) => acc + e?.quantity,
                  0
                ),
              })
              pixel.fbqEvent('AddToCart', {
                content_name: getTranslate(product.name),
                content_category: getTranslate(product.cat?.name),
                content_ids: product.code,
                content_type: 'product',
                value: product.finalPrice,
                currency: 'VND',
              })

              pushAddToCartEvent(
                product.code,
                getTranslate(product.name),
                product.price - product.finalPrice,
                product.price,
                1
              )
              resumeAutoApplyVoucher()
              const nextProductIds = collectProductIds(res?.data?.products)
              getCartCoupons(nextProductIds.length ? nextProductIds : undefined)
              setCartLoading(false)
              setProductLoading('')
              hideProgressBar()
              router.push(
                '/checkout/cart?type=preOrder&productIds=' + product._id
              )
              resolve(res)
            },
            onError: err => {
              fetchCart()
              setCartLoading(false)
              setProductLoading('')
              if (err && err instanceof Error) {
                if (err?.message == API_STATUS_CODE.PRODUCT_LIMIT_SALE) {
                  router.push(
                    '/checkout/cart?type=preOrder&productIds=' + product._id
                  )
                }
              }
              hideProgressBar()
              reject(err)
            },
          }
        )
      } else if (type === AddToCartType.INSTALLMENT) {
        addToInstallment(
          {
            product: cartItemData,
            quantity: 1,
            productIds: cartItemData.productId,
          },
          {
            onSuccess: async res => {
              setCart({
                ...res.data,
                quantity: res?.data?.products?.reduce(
                  (acc: number, e: CartItemType) => acc + e?.quantity,
                  0
                ),
              })
              resumeAutoApplyVoucher()
              const nextProductIds = collectProductIds(res?.data?.products)
              getCartCoupons(nextProductIds.length ? nextProductIds : undefined)

              pushAddToCartEvent(
                product.code || product.productId || product._id,
                getTranslate(product.name),
                product.finalPrice,
                (product.price || product.finalPrice) - product.finalPrice,
                1
              )
              setCartLoading(false)
              hideProgressBar()
              router.push(
                '/checkout/cart?type=installment&productIds=' + product._id
              )
              resolve(res)
            },
            onError: err => {
              fetchCart()
              setCartLoading(false)
              if (err && err instanceof Error) {
                if (err?.message == API_STATUS_CODE.PRODUCT_LIMIT_SALE) {
                  router.push(
                    '/checkout/cart?type=installment&productIds=' + product._id
                  )
                }
              }
              hideProgressBar()
              reject(err)
            },
          }
        )
      } else {
        addToCards(
          {
            product: cartItemData,
            quantity: 1,
          },
          {
            onSuccess: async res => {
              setCart({
                ...res.data,
                quantity: res?.data?.products?.reduce(
                  (acc: number, e: CartItemType) => acc + e?.quantity,
                  0
                ),
              })

              pixel.fbqEvent('AddToCart', {
                content_name: getTranslate(product.name),
                content_category: getTranslate(product.cat?.name),
                content_ids: product.code,
                content_type: 'product',
                value: product.finalPrice,
                currency: 'VND',
              })

              pushAddToCartEvent(
                product.code,
                getTranslate(product.name),
                product.price - product.finalPrice,
                product.price,
                1
              )
              fetchCart()
              setCartLoading(false)

              hideProgressBar()
              if (type === AddToCartType.BUY_NOW) {
                router.push('/checkout/cart')
              }
              const nextProductIds = collectProductIds(res?.data?.products)
              await getCartCoupons(
                nextProductIds.length ? nextProductIds : undefined
              )
              await resumeAutoApplyVoucher()
              resolve(res)
            },
            onError: err => {
              fetchCart()
              setCartLoading(false)
              if (err && err instanceof Error) {
                if (err?.message == API_STATUS_CODE.PRODUCT_LIMIT_SALE) {
                  router.push('/checkout/cart')
                }
              }
              hideProgressBar()
              reject(err)
            },
          }
        )
      }
    })
  }
  const addBundleToCart = async (data: Product[], type: string) => {
    await addBundleToCarts(
      {
        data,
      },
      {
        onSuccess: async res => {
          setCart({
            ...res.data,
            quantity: res?.data?.products?.reduce(
              (acc: number, e: CartItemType) => acc + e?.quantity,
              0
            ),
          })
          resumeAutoApplyVoucher()
          const nextProductIds = collectProductIds(res?.data?.products)
          getCartCoupons(nextProductIds.length ? nextProductIds : undefined)
          setCartLoading(false)
          hideProgressBar()
          if (type === AddToCartType.BUY_NOW) {
            router.push('/checkout/cart')
          }
        },
        onError: err => {
          fetchCart()
          hideProgressBar()
          setCartLoading(false)
          if (err && err instanceof Error) {
            if (err?.message == API_STATUS_CODE.PRODUCT_LIMIT_SALE) {
              router.push('/checkout/cart')
            }
          }
        },
      }
    )
  }
  const updateOrderInfo = () => true

  const changeQuantityItem = (
    product: CartItemType,
    quantity: number,
    gift: GiftItems[],
    service: OrderService[],
    shippingMethod: string,
    selectedReceiveAtWarehouse: boolean,
    type: string
  ) => {
    setCartLoading(true)
    const productCartData = {
      ...product,
      shippingMethod,
      serviceIds: service,
      giftItems: gift,
      selectedReceiveAtWarehouse: selectedReceiveAtWarehouse,
    }
    if (type === AddToCartType.PRE_ORDER) {
      addToPreOrder(
        {
          product: productCartData,
          quantity: quantity,
        },
        {
          onSuccess: async res => {
            setCart({
              ...res.data,
              quantity: res.data.products?.reduce(
                (acc: number, e: CartItemType) => acc + e?.quantity,
                0
              ),
            })
            const activeCoupons = resolveActiveCoupons()
            const shouldRemoveProductCoupons =
              quantity < 0 && product.quantity + quantity <= 0
            const sanitizedCoupons = shouldRemoveProductCoupons
              ? activeCoupons.filter(
                  couponKey =>
                    !couponKey?.includes(product.productId ?? 'unknown-product')
                )
              : activeCoupons
            const fetchParams: Record<string, unknown> = {
              wardCode: res.wardCode,
              shippingMethod: productCartData.shippingMethod,
              productIds: productCartData.productId,
              ...(isPreOrder ? { isPreOrder: true } : {}),
              action: 'cart container 1',
            }
            if (sanitizedCoupons.length) {
              fetchParams.coupon = sanitizedCoupons
            }

            safeFetchCart(fetchParams, 'update qty false')

            hideProgressBar()
            const nextProductIds = collectProductIds(res?.data?.products)
            getCartCoupons(nextProductIds.length ? nextProductIds : undefined)
            setCartLoading(false)
          },
          onError: err => {
            fetchCart({ isPreOrder: true })
            setCartLoading(false)
            if (err && err instanceof Error) {
              if (err?.message == API_STATUS_CODE.PRODUCT_LIMIT_SALE) {
                router.push('/checkout/cart?type=preOrder&id=' + product._id)
              }
            }
            hideProgressBar()
          },
        }
      )
    } else {
      addToCards(
        {
          product: productCartData,
          quantity: quantity,
        },
        {
          onSuccess: async res => {
            setCart({
              ...res.data,
              quantity: res.data.products?.reduce(
                (acc: number, e: CartItemType) => acc + e?.quantity,
                0
              ),
            })
            const activeCoupons = resolveActiveCoupons()
            const shouldRemoveProductCoupons =
              quantity < 0 && product.quantity + quantity <= 0
            const sanitizedCoupons = shouldRemoveProductCoupons
              ? activeCoupons.filter(
                  couponKey =>
                    !couponKey?.includes(product.productId ?? 'unknown-product')
                )
              : activeCoupons
            const fetchParams: Record<string, unknown> = {
              ...(isPreOrder ? { isPreOrder: true } : {}),
              action: 'cart container 2',
            }
            if (sanitizedCoupons.length) {
              fetchParams.coupon = sanitizedCoupons
            }

            safeFetchCart(fetchParams, 'update qty false')
            hideProgressBar()
            const nextProductIds = collectProductIds(res?.data?.products)
            getCartCoupons(nextProductIds.length ? nextProductIds : undefined)
            setCartLoading(false)
          },
          onError: err => {
            fetchCart()
            setCartLoading(false)
            if (err && err instanceof Error) {
              if (err?.message == API_STATUS_CODE.PRODUCT_LIMIT_SALE) {
                router.push('/checkout/cart')
              }
            }
            hideProgressBar()
          },
        }
      )
    }
  }

  const updateGiftAndService = (
    item: CartItemType,
    giftItems: GiftItems[] | undefined,
    services: OrderService[] | undefined,
    shippingMethod?: string,
    selectedReceiveAtWarehouse?: boolean,
    type?: string
  ) => {
    setCartLoading(true)
    const cartItemData = {
      code: item.code,
      name: item.name,
      shippingMethod: shippingMethod,
      price: item.price,
      slugs: item.slugs,
      productId: item.productId,
      quantity: item.quantity,
      finalPrice: item.finalPrice,
      giftItems: giftItems,
      serviceIds: services,
      selectedReceiveAtWarehouse: selectedReceiveAtWarehouse,
    }
    if (type === AddToCartType.PRE_ORDER) {
      addToPreOrder(
        {
          product: cartItemData as any,
          quantity: 0,
        },
        {
          onSuccess: async res => {
            setCart({
              ...res.data,
              quantity: res?.data?.products?.reduce(
                (acc: number, e: CartItemType) => acc + e?.quantity,
                0
              ),
            })
            const fetchParams: Record<string, unknown> = {
              wardCode: res.wardCode,
              shippingMethod: cartItemData.shippingMethod,
              productIds: cartItemData.productId,
              ...(isPreOrder ? { isPreOrder: true } : {}),
              ...(couponApply.length ? { coupon: couponApply } : {}),
              action: 'cart container 3',
            }
            safeFetchCart(fetchParams, 'update gift preOrder')
            hideProgressBar()
            setCartLoading(false)
          },
        }
      )
    }
    if (type === AddToCartType.INSTALLMENT) {
      addToInstallment(
        {
          product: cartItemData as any,
          quantity: 0,
          productIds: cartItemData.productId as string,
        },
        {
          onSuccess: async res => {
            setCart({
              ...res.data,
              quantity: res?.data?.products?.reduce(
                (acc: number, e: CartItemType) => acc + e?.quantity,
                0
              ),
            })
            const activeCoupons = resolveActiveCoupons()

            const fetchParams: Record<string, unknown> = {
              wardCode: res.wardCode,
              shippingMethod: cartItemData.shippingMethod,
              productIds: `${cartItemData.productId}:1`,
              ...(isPreOrder ? { isPreOrder: true } : {}),
              ...(activeCoupons.length ? { coupon: activeCoupons } : {}),
              action: 'cart container 4',
            }
            safeFetchCart(fetchParams, 'update gift installment')
            hideProgressBar()
            setCartLoading(false)
          },
        }
      )
    }
    if (
      type !== AddToCartType.PRE_ORDER &&
      type !== AddToCartType.INSTALLMENT
    ) {
      addToCards(
        {
          product: cartItemData as any,
          quantity: 0,
        },
        {
          onSuccess: async res => {
            setCart({
              ...res.data,
              quantity: res?.data?.products?.reduce(
                (acc: number, e: CartItemType) => acc + e?.quantity,
                0
              ),
            })
            const activeCoupons = resolveActiveCoupons()

            const fetchParams: Record<string, unknown> = {
              wardCode: res.wardCode,
              ...(activeCoupons.length ? { coupon: activeCoupons } : {}),
              ...(isPreOrder ? { isPreOrder: true } : {}),
              action: 'cart container 5',
            }
            safeFetchCart(fetchParams, 'update gift default')
            hideProgressBar()
            setCartLoading(false)
          },
        }
      )
    }
  }

  useEffect(() => {
    updateOrderInfo()
  }, [])

  useEffect(() => {
    if (router && router.query.orderNumberId) {
      api(`api/order/${router.query.orderNumberId}/detail`, {
        method: 'get',
        baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
      })
        .then(e => {
          if (e instanceof Error) {
            return router.push('/search-order')
          }
          setOrderInfo(e.data)
          return
        })
        .catch(() => {
          return router.push('/search-order')
        })
    }
  }, [router])

  const sendContact = async (values: {
    name: string
    phone: string
    productIds: string[]
  }) => {
    if (!values) {
      return
    }
    setCartLoading(true)
    const res = await api(`api/consultant/`, {
      method: 'POST',
      baseURL: process.env.NEXT_PUBLIC_ECOMMERCE_API_URL,
      data: values,
    })
    if (res instanceof Error) {
      setCartLoading(false)
      return res
    }
    setCartLoading(false)
    return res
  }

  function autoApplyCoupon(vouchers: VoucherItem[] = []) {
    if (!vouchers.length) {
      setCouponApply([])
      return []
    }

    const deriveVoucherScore = (voucher: VoucherItem) => {
      const fixedValue =
        typeof voucher.fixedValue === 'number' ? voucher.fixedValue : 0
      const maxDiscountValue =
        typeof voucher.maxDiscountValue === 'number'
          ? voucher.maxDiscountValue
          : 0
      const percentValue =
        typeof voucher.percentValue === 'number' ? voucher.percentValue : 0

      return Math.max(fixedValue, maxDiscountValue, percentValue)
    }

    const selectedByTarget = vouchers
      .filter(voucher => voucher.initialKey && !voucher.mes)
      .reduce<Record<string, VoucherItem>>((acc, voucher) => {
        if (!voucher.initialKey) return acc

        const key =
          voucher.target === CouponTarget.PRODUCT && voucher.product?._id
            ? `${CouponTarget.PRODUCT}:${voucher.product._id}`
            : CouponTarget.ORDER

        const current = acc[key]
        const incomingScore = deriveVoucherScore(voucher)
        const currentScore = current ? deriveVoucherScore(current) : -Infinity
        const incomingEnd = voucher.endTime
          ? new Date(voucher.endTime).getTime()
          : Number.POSITIVE_INFINITY
        const currentEnd = current?.endTime
          ? new Date(current.endTime).getTime()
          : Number.POSITIVE_INFINITY

        if (
          !current ||
          incomingScore > currentScore ||
          (incomingScore === currentScore && incomingEnd < currentEnd)
        ) {
          acc[key] = voucher
        }

        return acc
      }, {})

    const autoCoupons = Object.values(selectedByTarget)
      .map(voucher => voucher.initialKey)
      .filter((initialKey): initialKey is string => Boolean(initialKey))

    setCouponApply(autoCoupons)

    return autoCoupons
  }

  useEffect(() => {
    if (!cartCoupons?.length) {
      return
    }

    const snapshot = cartCoupons
      .map(voucher => {
        if (!voucher.initialKey) return null

        const status = voucher.mes ? 'invalid' : 'valid'

        return `${voucher.initialKey}:${status}`
      })
      .filter((value): value is string => Boolean(value))
      .sort()
      .join('|')

    if (skipAutoApply) {
      setAutoApplySnapshot(snapshot)
      return
    }

    if (snapshot && snapshot === autoApplySnapshot) {
      return
    }

    if (
      !autoApplySnapshot &&
      (voucherApply?.length > 0 || couponApply?.length > 0)
    ) {
      return
    }

    const autoCoupons = autoApplyCoupon(cartCoupons)

    setAutoApplySnapshot(snapshot)

    if (!autoCoupons.length) {
      return
    }

    setVoucherApply(autoCoupons)

    const fetchParams: Record<string, unknown> = {
      coupon: autoCoupons,
      ...(cart?.shippingMethod ? { shippingMethod: cart.shippingMethod } : {}),
      ...(isPreOrder ? { isPreOrder: true } : {}),
      action: 'cart auto apply voucher',
    }

    safeFetchCart(fetchParams, 'auto apply voucher')
  }, [
    cartCoupons,
    voucherApply?.length,
    couponApply?.length,
    fetchCart,
    cart?.shippingMethod,
    isPreOrder,
    setVoucherApply,
    autoApplySnapshot,
    safeFetchCart,
    skipAutoApply,
  ])

  useEffect(() => {
    if (voucherApply?.length === 0 && couponApply?.length === 0) {
      setAutoApplySnapshot('')
    }
  }, [voucherApply?.length, couponApply?.length])

  useEffect(() => {
    if (!skipAutoApply) {
      return
    }

    if (voucherApply?.length > 0 || couponApply?.length > 0) {
      setSkipAutoApply(false)
    }
  }, [skipAutoApply, voucherApply?.length, couponApply?.length])

  useEffect(() => {
    if (!cart?.products?.length) {
      setCartProductsSignature('')
      return
    }

    const totalAmount = cart.products.reduce((sum, product) => {
      const quantity = product?.quantity ?? 0
      const finalPrice = product?.finalPrice ?? 0

      return sum + quantity * finalPrice
    }, 0)
    const signature = cart.products
      .map(product => {
        if (!product?.productId) return null

        const quantity = product.quantity ?? 0
        const finalPrice = product.finalPrice ?? 0
        const price = product.price ?? 0

        return `${product.productId}:${quantity}:${price}:${finalPrice}`
      })
      .filter((value): value is string => Boolean(value))
      .sort()
      .join('|')
    const cartSignature = `${signature}|total:${totalAmount}`

    if (!signature || cartSignature === cartProductsSignature) {
      return
    }

    const currentProductIds = collectProductIds(cart.products)
    setCartProductsSignature(cartSignature)

    getCartCoupons(
      currentProductIds.length ? currentProductIds : undefined
    ).catch(error => {
      console.error('Fetch cart coupons failed:', error)
    })
  }, [cart?.products, cartProductsSignature, getCartCoupons])

  useEffect(() => {
    if (!cart?.coupons?.length) {
      if (invalidCouponSnapshot) {
        setInvalidCouponSnapshot('')
      }
      return
    }

    const validCoupons = cart.coupons
      .filter(
        couponItem =>
          !couponItem?.mes && typeof couponItem?.coupon?.initialKey === 'string'
      )
      .map(couponItem => couponItem.coupon.initialKey as string)
    const uniqueValidCoupons = Array.from(new Set(validCoupons))
    const invalidCoupons = cart.coupons.filter(
      couponItem =>
        couponItem?.mes && typeof couponItem?.coupon?.initialKey === 'string'
    )

    if (!invalidCoupons.length) {
      if (invalidCouponSnapshot) {
        setInvalidCouponSnapshot('')
      }
      return
    }

    const snapshot = invalidCoupons
      .map(couponItem => couponItem?.coupon?.initialKey ?? '')
      .filter(Boolean)
      .sort()
      .join('|')

    if (snapshot && snapshot === invalidCouponSnapshot) {
      return
    }

    setInvalidCouponSnapshot(snapshot)
    setVoucherApply(uniqueValidCoupons)
    setCouponApply(uniqueValidCoupons)

    const fetchParams: Record<string, unknown> = {
      ...(uniqueValidCoupons.length ? { coupon: uniqueValidCoupons } : {}),
      ...(cart?.shippingMethod ? { shippingMethod: cart.shippingMethod } : {}),
      ...(isPreOrder ? { isPreOrder: true } : {}),
      action: 'cart remove invalid voucher',
    }

    safeFetchCart(fetchParams, 'cart remove invalid voucher').finally(() => {
      setInvalidCouponSnapshot('')
    })
  }, [
    cart?.coupons,
    cart?.shippingMethod,
    isPreOrder,
    setVoucherApply,
    setCouponApply,
    safeFetchCart,
    invalidCouponSnapshot,
  ])

  return {
    addToCart,
    changeQuantityItem,
    timer,
    cartLoading,
    setCartLoading,
    getGuessTokenCart,
    setTimer,
    getOrderDetail,
    cancelOrder,
    confirmPayment,
    choosePaymentMethod,
    orderInfo,
    setOrderInfo,
    isInstallment,
    setIsInstallment,
    updateGiftAndService,
    chooseDestination,
    setGuestInfo,
    guestInfo,
    statusCode,
    getListCoupons,
    listCoupons,
    cartCoupons,
    setCartCoupons,
    getCartCoupons,
    sendContact,
    couponApply,
    setCouponApply,
    autoApplyCoupon,
    isPreOrder,
    setIsPreOrder,
    addBundleToCart,
    productLoading,
    paymentMethodCart,
    setPaymentMethodCart,
    clearAutoVoucherState,

    setVoucherApply,
    voucherApply,
    resumeAutoApplyVoucher,
  }
}

export const [CartProvider, useCart] = constate(CartContainer)
